import { ResourceClient, type HttpClient } from "@dl/web-core-js/api";

import type { SupplierPaymentTerms } from "./manufacturing";

/**
 * The settings tables, one single-row table per domain plus the two lists.
 *
 * A single-row table always holds exactly one row, id 1: the server refuses
 * to create or delete one, and forces the id on update. The lists (rank bands,
 * exchange rates) are ordinary resources.
 */

export type SalesRankingBasis = "VELOCITY" | "FOUR_WEEK_AVERAGE" | "TWELVE_WEEK_AVERAGE" | "TWELVE_WEEK_UNITS";

export const SALES_RANKING_BASES: SalesRankingBasis[] = [
  "VELOCITY",
  "FOUR_WEEK_AVERAGE",
  "TWELVE_WEEK_AVERAGE",
  "TWELVE_WEEK_UNITS",
];

/** Company-wide definitions: what a top seller is, how velocity is computed, what counts as new. */
export interface GeneralSettingsDTO {
  id: number;
  sales_ranking_basis: SalesRankingBasis;
  /** Weight of the 4-week average in the blended velocity, 0..1. */
  velocity_four_week_share: number;
  /** Weeks on sale before the blend applies; until then velocity is the 4-week average alone. */
  velocity_blend_after_weeks: number;
  /** A product is new for this many days after its first sale. */
  new_product_days: number;
}

/** Automatic discontinuation of slow movers. */
export interface ProductSettingsDTO {
  id: number;
  is_automatic_discontinuation_enabled: boolean;
  /** Days between the flag and the discontinuation. */
  discontinuation_delay_days: number;
  /** A single item selling fewer units a week than this is flagged. */
  discontinuation_single_item_threshold: number;
  /** Same for a bundle. */
  discontinuation_bundle_threshold: number;
}

/** Everything the production pipeline and the transfer planner read, bar the rank bands. */
export interface ProductionSettingsDTO {
  id: number;
  min_lot_size: number;
  max_lot_size: number;
  first_batch_lot_size: number;
  weight_urgency: number;
  weight_performance: number;
  paint_deficit_adjustment_in_weeks: number;
  new_product_safety_stock: number;
  transfer_cover_weeks: number;
  transfer_reserve_weeks: number;
  transfer_minimum_reserve_units: number;
  transfer_rounding_units: number;
  transfer_seed_units: number;
  /** Whether the nightly part lifecycle run sets part status from product status. */
  is_part_lifecycle_automatic: boolean;
  /** Days a kanban run takes from release to stock. */
  kanban_lead_time_days: number;
  /** Days between two runs of the same kanban part. */
  kanban_cycle_days: number;
  /** A ROOT user may turn pipeline lots into production orders despite shortages, until the go-live switches it off. */
  is_shortage_override_allowed: boolean;
  /** Off until cutover: on, the 3PL export, tracking, exports and feeds take each product's boxes from its version. */
  is_version_routing: boolean;
  /** Hours after the first sets arrive that a lot's split between assembly versions can still change. */
  assembly_split_window_hours: number;
}

/** The inventory rule job: its daily switch and how many top single items each market floors. */
export interface InventorySettingsDTO {
  id: number;
  /** When on, the nightly KPI recompute ends by setting every unlocked rule. */
  is_automation_enabled: boolean;
  /** Single items ranked this high or better on sales_rank_ca floor on Canadian channels; 0 = none. */
  floor_top_ca: number;
  /** The same for sales_rank_us and US channels. */
  floor_top_us: number;
  /** Also floor the Canadian channels' new products: first sold less than the new-product days ago, or never. */
  is_floor_new_ca: boolean;
  /** Also floor the US channels' new products. */
  is_floor_new_us: boolean;
  /** Also floor the Canadian channels' products with a quantity in production. */
  is_floor_in_production_ca: boolean;
  /** Also floor the US channels' products with a quantity in production. */
  is_floor_in_production_us: boolean;
}

/** What the pricing engine reads before it computes anything. */
export interface PricingSettingsDTO {
  id: number;
  gross_margin_percent: number;
  minimum_discount_net_margin_percent: number;
  /** ISO dates, inclusive. */
  reference_period_start_date: string;
  reference_period_end_date: string;
}

/**
 * Planning policy for a band of sales rank. A product takes the band with the
 * smallest `rank_to` at or above its rank; the band with no `rank_to` takes
 * everyone else, including products without a rank.
 */
export interface RankBandDTO {
  id: number;
  rank_to: number | null;
  reorder_weeks: number;
  target_weeks: number;
  safety_stock_quantity: number;
}

/** Purchasing: approval, planning defaults, supplier measurement, the cash projection and the reviews. */
export interface PurchasingSettingsDTO {
  id: number;
  /** Orders at or above this total in CAD wait for approval. */
  approval_threshold_amount: number;
  /** Days of usage the average daily usage is taken over. */
  usage_window_days: number;
  default_lead_time_days: number;
  default_order_cycle_days: number;
  /** Terms a new supplier starts with. Never DEPOSIT: that needs a percentage. */
  default_payment_terms: Exclude<SupplierPaymentTerms, "DEPOSIT">;
  /** Days after receipt for a net supplier with none typed. */
  default_payment_terms_days: number;
  /** Receipts a supplier needs before its measured lead time is trusted. */
  measured_lead_time_minimum_receipts: number;
  measured_lead_time_window_days: number;
  /** How far measured may stray from typed, in %, before the supplier is flagged. */
  lead_time_drift_percent: number;
  /** Days on hold after which an order is flagged. */
  hold_alert_days: number;
  /** An issued line expected longer ago than this is dead: not on order, not in the cash projection. */
  stale_order_days: number;
  cash_projection_weeks: number;
  /** Days the safety-days review looks back over. */
  review_window_days: number;
  /** The nightly run prepares draft orders by itself. */
  is_automatic_draft_enabled: boolean;
  /** The planning (the pipeline's lots) counts against positions as if reserved. Off by default (0074). */
  is_planning_in_position: boolean;
}

/**
 * The shop floor's settings, one row: how often boards and kiosks refresh, when
 * a kiosk forgets a badge, what counts as stalled, how often the backflush
 * sweep runs, what the lot documents print, and the Terrebonne packing line.
 */
export interface ShopFloorSettingsDTO {
  id: number;
  board_refresh_seconds: number;
  operator_idle_minutes: number;
  stalled_after_hours: number;
  backflush_interval_minutes: number;
  service_label_extra: number;
  production_sheet_copies: number;
  production_sheet_painted_copies: number;
  long_pallet_threshold_mm: number;
  cutlist_default_quantity: number;
  /** Inches per minute. */
  packing_line_speed: number;
  /** Inches between boxes. */
  packing_box_spacing: number;
  updated_at?: string | null;
}

export interface CurrencyRateDTO {
  id: number;
  currency: string;
  rate: number;
}

export const SETTINGS_PATHS = {
  general: "/settings/general",
  product: "/settings/product",
  production: "/settings/production",
  pricing: "/settings/pricing",
  purchasing: "/settings/purchasing",
  shopFloor: "/settings/shop-floor",
  inventory: "/settings/inventory",
  rankBands: "/settings/rank-band",
  rates: "/currency/exchange/rate",
} as const;

export function settingsClient<T extends { id: number }>(http: HttpClient, path: string) {
  return new ResourceClient<T>(http, path);
}

/** The one row of a single-row settings table. */
export async function fetchSingleRow<T extends { id: number }>(http: HttpClient, path: string): Promise<T> {
  const rows = await settingsClient<T>(http, path).findAll();
  const row = rows[0];
  if (!row) throw new Error(`${path} holds no row`);
  return row;
}

/** Shared cache keys, so a page that only reads a setting (bands on the pipeline) reuses the settings page's fetch. */
export const settingsKeys = {
  general: ["settings", "general"] as const,
  product: ["settings", "product"] as const,
  production: ["settings", "production"] as const,
  pricing: ["settings", "pricing"] as const,
  purchasing: ["settings", "purchasing"] as const,
  shopFloor: ["settings", "shop-floor"] as const,
  inventory: ["settings", "inventory"] as const,
  rankBands: ["settings", "rank-bands"] as const,
  rates: ["settings", "rates"] as const,
};

/** Bands in rank order, the open band last — the order the pipeline reads them in. */
export function sortBands(bands: RankBandDTO[]): RankBandDTO[] {
  return [...bands].sort((a, b) => (a.rank_to ?? Number.MAX_SAFE_INTEGER) - (b.rank_to ?? Number.MAX_SAFE_INTEGER));
}

/** A band's name, derived from its bound: "Top 25", or "Others" for the open band. Mirrors RankBands.label. */
export function bandLabel(band: RankBandDTO, t: (key: string, fallback: string, options?: Record<string, unknown>) => string): string {
  return band.rank_to === null ? t("settings.bands.others", "Others") : t("settings.bands.top", "Top {{rank}}", { rank: band.rank_to });
}

/** The band a rank falls in: the smallest `rank_to` at or above it, else the open band. Mirrors RankBands.bandFor. */
export function bandFor(bands: RankBandDTO[], rank: number | null | undefined): RankBandDTO | undefined {
  const sorted = sortBands(bands);
  if (rank !== null && rank !== undefined && rank > 0) {
    const bounded = sorted.find((band) => band.rank_to !== null && rank <= band.rank_to);
    if (bounded) return bounded;
  }
  return sorted.find((band) => band.rank_to === null);
}
